import React, { useState } from 'react';
import { Unlock, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons } from '../shared';
import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const UnlockPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [password, setPassword] = useState('');
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = (files) => {
        if (files && files.length > 0) {
            setFile(files[0]);
            toast.success('PDF loaded');
        }
    };

    const unlockPDF = async () => {
        if (!file) return;
        if (!password) {
            toast.warning('Please enter the password');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();

            setProgress(30);

            // Try to load with password
            const pdfDoc = await PDFDocument.load(arrayBuffer, {
                ignoreEncryption: true,
                password: password
            });

            setProgress(70);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_unlocked.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF unlocked! Opening preview...');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            if (error.message.includes('password') || error.message.includes('encrypted')) {
                toast.error('Incorrect password or PDF is not encrypted');
            } else {
                toast.error(`Failed to unlock PDF: ${error.message}`);
            }
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <Unlock size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Unlock PDF
                </h1>
                <p className="tool-description">
                    Remove password protection from PDF documents. Enter the password to unlock.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop password-protected PDF here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="warning" title="Password-Protected PDF">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPassword(''); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Enter Password
                            </h3>

                            <div>
                                <label className="label">PDF Password</label>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input"
                                    placeholder="Enter the PDF password"
                                    onKeyPress={(e) => e.key === 'Enter' && unlockPDF()}
                                />
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={unlockPDF} disabled={isProcessing || !password} loading={isProcessing} fullWidth>
                            <Unlock size={20} />
                            Unlock PDF
                        </Button>
                    </div>

                    <Alert variant="info" style={{ marginTop: 'var(--spacing-6)' }}>
                        <strong>Privacy:</strong> All processing happens in your browser. Your password and PDF never leave your device.
                    </Alert>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default UnlockPDF;
